import { URL } from './URL'

/** Custom scheme registered in app.json + AndroidManifest intent-filters. */
export const APP_SCHEME = 'pokerpower'

/**
 * Every deep link is normalized onto this host before it is stored, so
 * `InitialLinkingStore` (which does `new URL(value)`) always gets an absolute
 * URL whose `pathname` / `search` / `searchParams` are the router target.
 */
export const CANONICAL_HOST = 'pokerpower.com'

/**
 * https deep links we accept. Mirrors `ios.associatedDomains` in app.json and the
 * Android `intentFilters` in app.config.js, plus the Branch link domains. Anything
 * else is dropped rather than navigated to — a push payload is untrusted input.
 */
const TRUSTED_HOSTS = new Set([
  'pokerpower.com',
  'www.pokerpower.com',
  'app.pokerpower.com',
  'app.dev.pokerpower.com',
  'app.sandbox.pokerpower.com',
  'games.ppwr.io',
  'games-dev.ppwr.io',
  'gjva8.app.link',
  'gjva8-alternate.app.link',
  'gjva8.test-app.link',
  'gjva8-alternate.test-app.link'
])

/** Branch link domains. Their links carry the in-app route in `$deeplink_path`. */
const BRANCH_HOSTS = new Set([
  'gjva8.app.link',
  'gjva8-alternate.app.link',
  'gjva8.test-app.link',
  'gjva8-alternate.test-app.link'
])

/** Query keys that mark an Auth0/AuthSession callback rather than a navigable link. */
const AUTH_CALLBACK_PARAMS = ['code', 'state', 'error']

export type DeepLinkTarget = {
  /** expo-router href — path plus query string, e.g. `/invite/abc?redirectUri=/x`. */
  href: string
  /** Absolute canonical URL, safe to hand to `new URL()`. */
  url: string
}

export type DeepLinkSkipReason =
  | 'empty'
  | 'invalid_url'
  | 'untrusted_host'
  | 'unsupported_scheme'
  | 'auth_callback'
  | 'no_path'

export type DeepLinkResolution = { ok: true; target: DeepLinkTarget } | { ok: false; reason: DeepLinkSkipReason }

function buildTarget(pathWithQuery: string): DeepLinkTarget {
  const href = pathWithQuery.startsWith('/') ? pathWithQuery : `/${pathWithQuery}`
  return { href, url: `https://${CANONICAL_HOST}${href}` }
}

function isAuthCallback(search: string) {
  if (!search) return false
  const query = search.startsWith('?') ? search.slice(1) : search
  // Plain string match — RN's global URLSearchParams is not dependable across platforms.
  return query.split('&').some((pair) => AUTH_CALLBACK_PARAMS.includes(pair.split('=')[0].toLowerCase()))
}

/** Read one query param. Plain string parsing — RN's global URLSearchParams is not dependable. */
function getQueryParam(search: string, key: string): string | undefined {
  const query = search.startsWith('?') ? search.slice(1) : search
  try {
    for (const pair of query.split('&')) {
      const separatorIndex = pair.indexOf('=')
      if (separatorIndex === -1) continue
      if (decodeURIComponent(pair.slice(0, separatorIndex)) === key) {
        return decodeURIComponent(pair.slice(separatorIndex + 1).replace(/\+/g, ' '))
      }
    }
  } catch {
    // Malformed percent-encoding — treat as absent rather than throwing on untrusted input.
  }
  return undefined
}

function fromPathAndSearch(path: string, search: string): DeepLinkResolution {
  const normalized = path && path !== '/' ? path : ''
  // A bare `pokerpower://?code=…` is the AuthSession redirect coming back, not a link.
  if (!normalized) {
    return { ok: false, reason: isAuthCallback(search) ? 'auth_callback' : 'no_path' }
  }
  return { ok: true, target: buildTarget(`${normalized}${search}`) }
}

/** The in-app route of a Branch long link, e.g. `https://gjva8.app.link/?$deeplink_path=lounges/join?code=X`. */
function getBranchDeepLinkPath(host: string, search: string): string | undefined {
  if (!BRANCH_HOSTS.has(host)) return undefined
  const branchPath = getQueryParam(search, '$deeplink_path')
  if (!branchPath) return undefined
  return branchPath.startsWith('/') ? branchPath : `/${branchPath}`
}

function fromWebUrl(host: string, path: string, search: string): DeepLinkResolution {
  if (!TRUSTED_HOSTS.has(host)) {
    return { ok: false, reason: 'untrusted_host' }
  }
  const branchPath = getBranchDeepLinkPath(host, search)
  if (branchPath) {
    return resolveDeepLink(branchPath)
  }
  return fromPathAndSearch(path, search)
}

/**
 * Router href for a Branch long link, or `undefined` for anything else. Lets expo-router's
 * `redirectSystemPath` open the app on the link's route instead of the Branch link's own `/`.
 */
export function resolveBranchLink(raw?: string | null): string | undefined {
  const value = raw?.trim()
  if (!value || !/^https?:\/\//i.test(value)) return undefined
  let parsed: InstanceType<typeof URL>
  try {
    parsed = new URL(value)
  } catch {
    return undefined
  }
  const branchPath = getBranchDeepLinkPath(parsed.host.toLowerCase(), parsed.search)
  if (!branchPath) return undefined
  const resolution = resolveDeepLink(branchPath)
  return resolution.ok ? resolution.target.href : undefined
}

/**
 * Normalize a deep link from any push provider into a router target.
 *
 * Accepts all the shapes our campaigns actually send:
 * - A bare router path (`/invite/abc`)
 * - Customer.io link: `pokerpower://invite/abc`
 * - Universal / Branch link: `https://pokerpower.com/invite/abc?x=1`
 *
 * Custom-scheme URLs need care: WHATWG parses `pokerpower://invite/abc` as
 * host=`invite`, pathname=`/abc`, so reading `pathname` alone silently drops the
 * first segment. We rejoin host + pathname to recover the real route.
 */
export function resolveDeepLink(raw?: string | null): DeepLinkResolution {
  const value = raw?.trim()
  if (!value) return { ok: false, reason: 'empty' }

  // A bare expo-router path is not a parseable URL.
  if (value.startsWith('/')) {
    const queryIndex = value.indexOf('?')
    const path = queryIndex === -1 ? value : value.slice(0, queryIndex)
    const search = queryIndex === -1 ? '' : value.slice(queryIndex)
    return fromPathAndSearch(path, search)
  }

  // `URL` is a value export (polyfill on native, window.URL on web), so derive the type.
  let parsed: InstanceType<typeof URL>
  try {
    parsed = new URL(value)
  } catch {
    return { ok: false, reason: 'invalid_url' }
  }

  const scheme = parsed.protocol.replace(':', '').toLowerCase()

  if (scheme === APP_SCHEME) {
    const host = parsed.host ? `/${parsed.host}` : ''
    return fromPathAndSearch(`${host}${parsed.pathname}`, parsed.search)
  }

  if (scheme === 'http' || scheme === 'https') {
    return fromWebUrl(parsed.host.toLowerCase(), parsed.pathname, parsed.search)
  }

  return { ok: false, reason: 'unsupported_scheme' }
}
